import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import type { Qualification } from 'src/__generated__/graphql';
import {
  Page,
  PageSection,
  PageSectionState,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useEffectOnConditionOnce } from 'src/context/use-effect-on-condition-once';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useProfileAnalytic } from 'src/hooks/useProfileAnalytic';
import {
  IMPRESSION_PROFILE_CERTSY,
  TrackingVerificationStatus,
} from 'src/modules/analytics.types';

const TRACKING_INFO = {
  currentPageSection: PageSection.EDUCATION,
  pageTitle: 'profile+education',
  profileUpdateSource: 'steps',
  actionOrigin: 'steps:applicationForm',
};

export function useTracking({
  qualifications,
  editingId,
}: {
  qualifications: Qualification[];
  editingId: string | null;
}) {
  const [listTrackingRef, inView] = useInView();
  const trackingClient = useTrackingClient();
  const currentPage = useGetCurrentPage();

  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();

  const currentQualification = qualifications.find((q) => q.id === editingId);

  let verificationStatus = TrackingVerificationStatus.UNVERIFIABLE;
  if (currentQualification?.credential) {
    verificationStatus = TrackingVerificationStatus.VERIFIED;
  } else if (currentQualification?.verificationUrl) {
    verificationStatus = TrackingVerificationStatus.NOT_VERIFIED;
  }

  // private, dont' return
  const trackLink = (info: Parameters<typeof trackingClient.trackLink>[0]) =>
    trackingClient.trackLink({
      profileApproachabilityStatus,
      profileSharedStatus,
      profileVisibilityLevel,
      currentPage,
      ...TRACKING_INFO,
      ...info,
    });

  useEffectOnConditionOnce(() => {
    const qualificationsVerifiedTotal = qualifications.filter((q) =>
      Boolean(q.credential),
    ).length;

    const qualificationsNotVerifiedTotal = qualifications.filter(
      (q) => !Boolean(q.credential) && Boolean(q.verificationUrl),
    ).length;

    const qualificationsWithoutVerificationUrl = qualifications.filter(
      (q) => !Boolean(q.verificationUrl),
    ).length;

    trackLink({
      link: IMPRESSION_PROFILE_CERTSY,
      eventName: IMPRESSION_PROFILE_CERTSY,
      currentPageSection: PageSection.CERTSY,
      qualificationsVerifiedTotal,
      qualificationsNotVerifiedTotal,
      qualificationsUnverifiableTotal:
        qualificationsWithoutVerificationUrl - qualificationsVerifiedTotal,
    });
  }, [inView]);

  useEffect(() => {
    if (editingId && currentQualification) {
      trackingClient.trackView({
        ...TRACKING_INFO,
        currentPage,
        eventName: 'profile_section_displayed',
        view: Page.PROFILE_PAGE,
        currentPageSectionState: PageSectionState.EDIT,
        certsyType: 'qualification',
        verificationStatus,
      });
    }
    if (editingId && !currentQualification) {
      trackingClient.trackView({
        ...TRACKING_INFO,
        currentPage,
        eventName: 'profile_section_displayed',
        view: Page.PROFILE_PAGE,
        currentPageSectionState: PageSectionState.ADD,
      });
    }
  }, [
    trackingClient,
    currentQualification,
    editingId,
    verificationStatus,
    currentPage,
  ]);

  const trackAddQualification = (nudgeType?: 'cv parsed') => {
    const eventName = nudgeType
      ? ['profile_update_succeeded', 'nudge_pressed']
      : ['profile_update_succeeded'];

    trackLink({
      link: 'profile-education-add',
      eventName,
      nudgeType,
      updateType: UpdateType.ADD,
    });
  };

  const trackEditQualification = (nudgeType?: 'cv parsed') => {
    const eventName = nudgeType
      ? ['profile_update_succeeded', 'nudge_pressed']
      : ['profile_update_succeeded'];

    trackLink({
      link: 'profile-education-edit',
      eventName,
      nudgeType,
      updateType: UpdateType.EDIT,
      certsyType: 'qualification',
      verificationStatus,
    });
  };

  const trackRejectQualification = () => {
    trackLink({
      link: 'cv-parsing-nudge-education-dismiss',
      eventName: 'nudge_dismiss_pressed',
      nudgeType: 'cv parsing',
      certsyType: 'qualification',
      verificationStatus: TrackingVerificationStatus.UNVERIFIABLE,
    });
  };

  const trackDeleteQualification = () => {
    trackLink({
      link: 'education_delete_pressed',
      eventName: ['profile_update_succeeded'],
      updateType: UpdateType.DELETE,
      certsyType: 'qualification',
      verificationStatus,
    });
  };

  const trackDeleteVerifiedQualification = () => {
    trackLink({
      link: 'education_delete_pressed',
      eventName: ['education_delete_pressed'],
      certsyType: 'qualification',
      verificationStatus,
    });
  };

  return {
    trackDeleteVerifiedQualification,
    trackAddQualification,
    trackEditQualification,
    trackRejectQualification,
    trackDeleteQualification,
    listTrackingRef,
  };
}
